import type {ReactElement} from 'react';
import {useEffect, useRef} from 'react';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import type {BaseControlledListItem, ControlledListItem3ActionProps} from '@Components/controlled-list/controlled-list.types';
import {ControlledListItemSize} from '@Components/controlled-list/controlled-list.types';
import Divider from '@Components/divider';
import {Thumbnail} from '@Components/thumbnail';
import type {ThumbnailData} from '@Components/thumbnail/thumbnail.types';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import styles from './controlled-list-item-type-3.module.scss';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';

export interface ControlledListItemType3Props extends BaseControlledListItemType3 {
  subItems?: BaseControlledListItemType3[];
  scrollIntoViewIfSelected?: boolean;
  shouldScrollIntoView?: boolean;
  scrollIntoViewBlockProperty?: ScrollLogicalPosition;
  textClass?: string;
  subtextClass?: string;
  isDisabled?: boolean;
  displayWarning?: ReactElement<any>;
  trailingContent?: ReactElement;
}

export interface BaseControlledListItemType3 extends BaseControlledListItem {
  icon?: string;
  subText?: string;
  actions?: ControlledListItem3ActionProps[];
  containsDangerouslySetText?: boolean;
  showSelectionCheckmark?: boolean;
  thumbnail?: ThumbnailData;
  thumbnailClassName?: string;
  isItemUnmargined?: boolean;
  textContainerClass?: string;

  onClickSubItem?(id: string, currentSelectionState?: boolean): void;
}

export function ControlledListItemType3({
  id = '',
  className = '',
  isSelected = false,
  icon = '',
  subText = '',
  subItems = [],
  onClick = $.noop,
  onClickSubItem = $.noop,
  containsDangerouslySetText = false,
  showSelectionCheckmark = true,
  listSize = ControlledListItemSize.MEDIUM,
  scrollIntoViewIfSelected = false,
  shouldScrollIntoView = false,
  scrollIntoViewBlockProperty = 'center',
  textClass = '',
  textContainerClass = '',
  subtextClass = '',
  isDisabled = false,
  isLoading = false,
  ...props
}: ControlledListItemType3Props) {
  const ref = useRef<HTMLLIElement>(null);

  useEffect((): void => {
    if (ref.current && ((scrollIntoViewIfSelected && isSelected) || shouldScrollIntoView)) {
      ref.current.scrollIntoView({behavior: 'smooth', block: scrollIntoViewBlockProperty});
    }
  }, [ref, shouldScrollIntoView]);

  const getClassnameForSubItem = (isSubitemSelected?: boolean): string => {
    return `${styles.subItemContainer} ${
      isSubitemSelected ? styles.selectedSubItemContainer : ''
    } radius-4 spacing-p-0 spacing-m-t-2 spacing-m-l-6 flex-justify-between flex-items-center`;
  };

  const showSubItems = () => {
    if (isSelected && subItems?.length > 0) {
      return (
        <ul className={`${styles.subItems} spacing-m-0 spacing-m-b-1`}>
          {subItems.map((record) => {
            return (
              <li
                onClick={() => {
                  onClickSubItem(record.id, record.isSelected ? record.isSelected : false);
                }}
                key={record.id}
                className={getClassnameForSubItem(record.isSelected)}
              >
                <Text className={`spacing-m-l-3 ${record.isSelected ? 'content-primary' : 'content-sub-text'}`} val={record.text} size={TextSize.XSMALL} bold={record.isSelected} />
              </li>
            );
          })}
          <Divider additionalClasses="spacing-m-t-2" />
        </ul>
      );
    }
    return null;
  };

  const getSelectedCheck = () => {
    return (
      <div className="flexbox spacing-m-r-3">
        <Icon className={styles.icon} icon="icon-check" size={IconSize.SIZE_ICON_20} />
      </div>
    );
  };

  const getTrailingContent = (): ReactElement | null => {
    if (!props.trailingContent) {
      return null;
    }

    return <div className="flexbox spacing-m-r-3">{props.trailingContent}</div>;
  };

  const getCTAForListItemAction = (action: ControlledListItem3ActionProps, index: number) => {
    const {containerClasses, ...iconProps} = action;
    return (
      <div key={index} className={containerClasses}>
        <Icon {...iconProps} />
      </div>
    );
  };

  const displayThumbnail = (): ReactElement<any> | null => {
    if (props.thumbnail) {
      return <Thumbnail className={`${props.thumbnail.type !== ThumbnailType.ICON ? styles.image : ''} ${props.thumbnailClassName} spacing-m-r-2`} data={props.thumbnail} />;
    }

    return null;
  };

  const getSpacingClasses = (): string => {
    if (listSize === ControlledListItemSize.SMALL) {
      return 'spacing-p-l-3 spacing-p-r-3 spacing-p-t-2 spacing-p-b-2';
    }

    if (props.thumbnail !== undefined) {
      return 'spacing-p-2';
    }

    return 'spacing-p-3';
  };

  const getContainerClasses = (): string => {
    const classes = `${styles.container} flex-column-align-center ${props.isItemUnmargined ? '' : 'spacing-m-b-2'} spacing-p-0`;

    if (isDisabled) {
      return `${classes} ${styles.disabledContainer}`;
    }

    return classes;
  };

  const getTextClasses = (): string => {
    let classes = `-truncated-text _full-width ${textClass}`;

    if (isDisabled) {
      classes += ' content-disabled';
    } else if (isSelected) {
      classes += ' content-primary';
    } else {
      classes += ' content-body';
    }

    return classes;
  };

  const getSubTextClasses = (): string => {
    let classes = subtextClass;

    if (isDisabled) {
      classes += ' content-disabled';
    } else if (isSelected) {
      classes += ' content-primary';
    } else {
      classes += ' content-sub-text';
    }

    return classes;
  };

  const showListItem = () => {
    return (
      <li ref={ref} className={getContainerClasses()} aria-disabled={isDisabled}>
        <div className={`${styles.contentContainer} flex-row-align-center`}>
          <div
            onClick={(e) => {
              onClick(id, e);
            }}
            className={`${styles.listItemContainer} spacing-p-0 flex-justify-between flex-items-center ${className} ${isSelected ? styles.selectedListItemContainer : ''} radius-4`}
          >
            <div className={`${styles.textContainer} ${textContainerClass} ${getSpacingClasses()}`}>
              <div className="flex-row-align-center _full-width">
                {displayThumbnail()}
                <div className="flexbox _full-width">
                  <Text
                    className={getTextClasses()}
                    val={props.text}
                    size={TextSize.XSMALL}
                    bold={isSelected}
                    {...(containsDangerouslySetText && {dangerouslySetInnerHTML: true})}
                  />
                </div>
              </div>
              {subText ? <Text className={getSubTextClasses()} val={subText} size={TextSize.XXSMALL} bold={isSelected} /> : null}
            </div>
            {getTrailingContent()}
            {showSelectionCheckmark && isSelected && !isLoading ? getSelectedCheck() : null}
            {isLoading ? <CircularProgressLoader classes="spacing-m-2" size={LOADER_SIZE.XSMALL} /> : null}
          </div>
          {typeof props.actions !== 'undefined' ? props.actions.map(getCTAForListItemAction) : null}
        </div>
        {props.displayWarning}
        {showSubItems()}
      </li>
    );
  };

  return <>{showListItem()}</>;
}
